import { useEffect, useState } from "react";
import { BookOpen, Crown, GripVertical, HeartHandshake, HelpCircle, Home, LayoutDashboard, LogIn, LogOut, Play, RefreshCcw, Rocket, Settings, Sparkles, UserRound, Wrench } from "lucide-react";
import { getSiteVisuals } from "../lib/api";
import { PATREON_JOIN_URL } from "../lib/patreon-links";
import { AdminMe, MeResponse, SiteVisuals } from "../lib/types";

interface Props {
  me: MeResponse | null;
  admin?: AdminMe | null;
  children: React.ReactNode;
}

type NavigationItem = SiteVisuals["navigationItems"][number];

const defaultNav: NavigationItem[] = [
  { href: "/", label: "Home", icon: "home", enabled: true },
  { href: "/start-here", label: "Start", icon: "sparkles", enabled: true, mobileOptional: true },
  { href: "/library", label: "Browse", icon: "book", enabled: true },
  { href: "/series", label: "Series", icon: "crown", enabled: true },
  { href: "patreon_join", label: "Join", icon: "heart", enabled: true },
  { href: "/account", label: "Account", icon: "user", enabled: true }
];
const navIconMap = {
  home: Home,
  sparkles: Sparkles,
  book: BookOpen,
  crown: Crown,
  heart: HeartHandshake,
  user: UserRound
} as const;

function usableNavItems(items?: NavigationItem[]) {
  const source = items?.length ? items : defaultNav;
  const enabled = source.filter((item) => item.enabled !== false);
  return enabled.length ? enabled : defaultNav;
}

function navHref(item: NavigationItem) {
  return item.href === "patreon_join" ? PATREON_JOIN_URL : item.href;
}

function currentPath() {
  if (typeof window === "undefined") return "/";
  return `${window.location.pathname}${window.location.search}`;
}

function reorderHref() {
  if (typeof window === "undefined") return "/";
  const url = new URL(window.location.href);
  url.searchParams.set("adminReorder", "1");
  return `${url.pathname}${url.search}`;
}

function editHref() {
  if (typeof window === "undefined") return "/admin";
  const path = window.location.pathname;
  if (path === "/") return "/admin";
  if (path.startsWith("/episode/")) return `/admin?tab=episodes&episode=${encodeURIComponent(path.split("/").filter(Boolean)[1] || "")}`;
  if (path.startsWith("/series")) return "/admin?tab=series";
  if (path.startsWith("/library")) return "/admin?tab=episodes";
  if (path.startsWith("/start-here")) return "/admin";
  if (path.startsWith("/account") || path.startsWith("/login")) return "/admin";
  return "/admin";
}

function AdminControlBar({ admin }: { admin?: AdminMe | null }) {
  const [hidden, setHidden] = useState(false);
  if (!admin?.authenticated) return null;
  if (hidden) return (
    <aside className="admin-control-bar compact-admin-bar" aria-label="Admin controls hidden">
      <strong><Settings size={17} />Admin Mode ON</strong>
      <button type="button" onClick={() => setHidden(false)}>Show controls</button>
      <a href="/admin"><LayoutDashboard size={16} />Control Room</a>
    </aside>
  );
  const path = currentPath();
  return (
    <aside className="admin-control-bar" aria-label="Admin controls">
      <strong><Settings size={17} />Admin Mode ON</strong>
      <a href="/admin"><LayoutDashboard size={16} />Control Room</a>
      <a href="/admin?tab=sync"><RefreshCcw size={16} />Update</a>
      <a href={editHref()}><Play size={16} />Organize</a>
      <a href="/admin?tab=series"><BookOpen size={16} />Series</a>
      <a href="/admin/fix"><Wrench size={16} />Fix</a>
      <a href="/admin/launch"><Rocket size={16} />Launch</a>
      <a href="/admin/help"><HelpCircle size={16} />Manual</a>
      <a href={reorderHref()}><GripVertical size={16} />Reorder this page</a>
      <button type="button" onClick={() => setHidden(true)}>Hide controls</button>
      {path.includes("adminReorder=1") ? <span>Reorder mode is on</span> : null}
    </aside>
  );
}

export function Layout({ me, admin, children }: Props) {
  const authenticated = me?.authenticated;
  const [navItems, setNavItems] = useState<NavigationItem[]>(defaultNav);
  useEffect(() => {
    getSiteVisuals().then(({ visuals }) => setNavItems(usableNavItems(visuals.navigationItems))).catch(() => undefined);
  }, []);
  return (
    <div className="shell">
      <header className="topbar">
        <a className="brand" href="/" aria-label="YaoiGen Portal home">
          <span className="brand-mark">YG</span>
          <span>YaoiGen Portal</span>
        </a>
        <nav className="nav">
          {usableNavItems(navItems).map((item) => {
            const Icon = navIconMap[item.icon] || BookOpen;
            const href = navHref(item);
            return (
              <a key={item.href} className={item.mobileOptional ? "mobile-optional" : undefined} href={href} title={item.label} rel={href.startsWith("http") ? "noreferrer" : undefined}>
                <Icon size={18} aria-hidden="true" />
                <span>{item.label}</span>
              </a>
            );
          })}
        </nav>
        <a className="session-pill" href={authenticated ? "/logout" : "/login"}>
          {authenticated ? <LogOut size={17} /> : <LogIn size={17} />}
          <span>{authenticated ? me.accessLevel.replace("_", " ") : "Connect"}</span>
        </a>
      </header>
      <AdminControlBar admin={admin} />
      <main>{children}</main>
    </div>
  );
}
